import { zodResolver } from '@hookform/resolvers/zod';
import type { Branch, BranchType } from '@packages/shared-types';
import { Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { TableActionsMenu, type TableAction } from '@/components/shared/TableActionsMenu';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import {
  getAvailableBranchTypes,
  getBranchTypeTranslationKey,
} from '@/utils/branchTypes';
import {
  listBranchesPaginated,
  createBranch,
  type CreateBranchPayload,
} from '@/services/branchService';

const branchFormSchema = z.object({
  name: z.string().min(1, 'pages.branches.form.nameRequired'),
  type: z.enum(['retail_store', 'warehouse', 'distribution_center'], {
    message: 'pages.branches.form.typeRequired',
  }),
  address: z.string().optional(),
});

type BranchFormValues = z.infer<typeof branchFormSchema>;

const BRANCH_TYPES: Array<{ value: BranchType; labelKey: string }> = [
  { value: 'retail_store', labelKey: 'pages.branches.form.typeOptions.retail_store' },
  { value: 'warehouse', labelKey: 'pages.branches.form.typeOptions.warehouse' },
  {
    value: 'distribution_center',
    labelKey: 'pages.branches.form.typeOptions.distribution_center',
  },
];

export function BranchesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const title = useIndustryTranslation('pages.branches.title');
  const description = useIndustryTranslation('pages.branches.description');
  const createLabel = useIndustryTranslation('pages.branches.actions.create');
  const emptyLabel = useIndustryTranslation('pages.branches.list.empty');
  const loadingLabel = useIndustryTranslation('pages.branches.list.loading');
  const successCreate = useIndustryTranslation('pages.branches.success.create');
  const errorLoad = useIndustryTranslation('pages.branches.error.load');
  const errorCreate = useIndustryTranslation('pages.branches.error.create');

  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const pagination = usePaginationFromUrl();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    control: controlCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
  } = useForm<BranchFormValues>({
    resolver: zodResolver(branchFormSchema),
    defaultValues: {
      name: '',
      type: 'retail_store',
      address: '',
    },
  });

  const loadBranches = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listBranchesPaginated({
        page: pagination.page,
        limit: pagination.limit,
      });
      setBranches(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [errorLoad, pagination.page, pagination.limit, activeTenant?.id]);

  useEffect(() => {
    void loadBranches();
  }, [loadBranches]);

  const handleCreate = async (values: BranchFormValues): Promise<void> => {
    try {
      const payload: CreateBranchPayload = {
        name: values.name,
        type: values.type,
        address: values.address || undefined,
      };
      const newBranch = await createBranch(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      toast.success(successCreate);
      // Navigate to branch edit page
      navigate(`/branches/${newBranch.id}/edit`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorCreate;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleRowClick = useCallback(
    (branchId: string) => {
      return () => navigate(`/branches/${branchId}/edit`);
    },
    [navigate],
  );

  const handleEditClick = useCallback(
    (branchId: string) => {
      return () => navigate(`/branches/${branchId}/edit`);
    },
    [navigate],
  );

  const getBranchTypeLabel = (type: BranchType): string => {
    const industry = activeTenant?.industry;
    const key = getBranchTypeTranslationKey(type, industry);
    const translated = t(key);
    // Fallback to base key if industry-specific translation doesn't exist
    return translated !== key ? translated : t(`pages.branches.form.typeOptions.${type}`);
  };

  const availableBranchTypes = useMemo(() => {
    const allowedTypes = getAvailableBranchTypes(activeTenant?.industry);
    return BRANCH_TYPES.filter((bt) => allowedTypes.includes(bt.value));
  }, [activeTenant?.industry]);

  const createDialogTitle = useIndustryTranslation(
    'pages.branches.dialogs.create.title',
  );
  const createDialogDescription = useIndustryTranslation(
    'pages.branches.dialogs.create.description',
  );

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            {createLabel}
          </Button>
        }
      />

      {error && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </div>
        ) : branches.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.branches.table.name')}</TableHead>
                <TableHead>{t('pages.branches.table.type')}</TableHead>
                <TableHead>{t('pages.branches.table.address')}</TableHead>
                <TableHead>{t('pages.branches.table.created')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((branch) => {
                const actions: TableAction[] = [
                  {
                    label: t('pages.branches.actions.edit'),
                    icon: Pencil,
                    onClick: handleEditClick(branch.id),
                  },
                ];
                return (
                  <TableRow
                    key={branch.id}
                    onRowClick={handleRowClick(branch.id)}
                  >
                  <TableCell className="font-medium">{branch.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {getBranchTypeLabel(branch.type)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {branch.address || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(branch.createdAt)}
                  </TableCell>
                  <TableCell>
                    <TableActionsMenu actions={actions} />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            pagination={pagination}
            rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
            pageOfLabel={t('pages.common.pagination.pageOf', {
              current: pagination.page,
              total: pagination.totalPages,
            })}
            onRefresh={() => void loadBranches()}
            isRefreshing={isLoading}
          />
          </>
        )}
      </TableCard>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createDialogTitle}</DialogTitle>
            <DialogDescription>{createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCreate(handleCreate)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  {t('pages.branches.form.name')} *
                </Label>
                <Input
                  id="create-name"
                  {...registerCreate('name')}
                  placeholder={t('pages.branches.form.name')}
                />
                {createErrors.name && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.name.message || 'pages.branches.form.nameRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-type">
                  {t('pages.branches.form.type')} *
                </Label>
                <Controller
                  name="type"
                  control={controlCreate}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger id="create-type">
                        <SelectValue
                          placeholder={t('pages.branches.form.selectType')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableBranchTypes.map((branchType) => {
                          const industry = activeTenant?.industry;
                          const translationKey = getBranchTypeTranslationKey(
                            branchType.value,
                            industry,
                          );
                          const translated = t(translationKey);
                          const label =
                            translated !== translationKey
                              ? translated
                              : t(branchType.labelKey);
                          return (
                            <SelectItem
                              key={branchType.value}
                              value={branchType.value}
                            >
                              {label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                />
                {createErrors.type && (
                  <p className="text-sm text-destructive">
                    {t(createErrors.type.message || 'pages.branches.form.typeRequired')}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-address">
                  {t('pages.branches.form.address')}
                </Label>
                <Input
                  id="create-address"
                  {...registerCreate('address')}
                  placeholder={t('pages.branches.form.address')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreate();
                }}
              >
                {t('pages.branches.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating
                  ? t('pages.branches.actions.saving')
                  : createLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
